import { ThankYouPageContainer } from "@components/donate/DonateV3/PaymentProcess/components/PageContainer";
import { AddComment } from "@components/donate/DonateV3/PaymentProcess/pages/DonationThankYou/AddComment";
import { DonationLogin } from "@components/donate/DonateV3/PaymentProcess/pages/DonationThankYou/DonationLogin";
import { Signup } from "@components/donate/DonateV3/PaymentProcess/pages/DonationThankYou/Signup";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import {
  CreateOrUpdateDonationResult,
  DonateFormType,
  PaymentProcessProps,
} from "@components/donate/DonateV3/types";
import { usePostDonationAction } from "@components/donate/DonateV3/usePostDonationAction";
import { css } from "@emotion/react";
import { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { commentTextCodec } from "@every.org/common/src/codecs/text";
import { PaymentMethod } from "@every.org/common/src/entity/types";
import { DONATE_HASH } from "@every.org/common/src/helpers/clientRoutes";

import {
  useABTestingId,
  useIsLoggedIn,
  useLoggedInOrGuestUserOrUndefined,
} from "src/context/AuthContext/hooks";
import { colorCssVars } from "src/theme/color";
import { verticalStackCss } from "src/theme/spacing";
import { FontWeight, textSizeCss } from "src/theme/text";
import { getWindow } from "src/utility/window";

type DonationConfirmationProps = {
  donateModalProps: PaymentProcessProps;
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  createOrUpdateDonationResult?: CreateOrUpdateDonationResult;
};

export const getEmailReceiptMessage = (
  paymentsMethod: PaymentMethod,
  brokeragePull?: boolean
) =>
  paymentsMethod === PaymentMethod.STOCKS && brokeragePull
    ? "We are sending your donation request to your broker."
    : `We will email your receipt${
        [
          PaymentMethod.CRYPTO,
          PaymentMethod.DAF,
          PaymentMethod.STOCKS,
          PaymentMethod.MUTUAL_FUND,
          // A CHECK reaching this page is always a donor QCD (IRA) pledge: the
          // donate flow sets CHECK only for the IRA option and always marks it
          // isQCD, and staff-recorded non-QCD checks are created PAID and never
          // render the thank-you page. Nothing has been received yet — the donor
          // still has to have their custodian mail the check — so the receipt
          // must be promised conditionally, as it is for the other pledges.
          PaymentMethod.CHECK,
        ].includes(paymentsMethod)
          ? " once your transaction is confirmed"
          : ""
      }.`;

export const ThankYouMessage = (props: {
  paymentMethod: PaymentMethod;
  brokeragePull?: boolean;
}) => (
  <div css={[verticalStackCss.xxs, { width: "100%" }]}>
    <strong
      css={[
        textSizeCss.xl,
        css`
          font-weight: ${FontWeight.BOLD};
        `,
      ]}
    >
      Thank you!{" "}
    </strong>
    <span css={{ color: `var(${colorCssVars.text.secondary})` }}>
      {getEmailReceiptMessage(props.paymentMethod, props.brokeragePull)}
    </span>
  </div>
);

export const DonationThankYou = ({
  donateModalProps: {
    defaultComment,
    nonprofit,
    fundraiser,
    redirectUrl,
    onComplete,
    donateAction,
  },
  form,
  formContext,
  createOrUpdateDonationResult,
}: DonationConfirmationProps) => {
  const abTestingId = useABTestingId();
  const loggedInOrGuestUser = useLoggedInOrGuestUserOrUndefined();
  const isLoggedIn = useIsLoggedIn();
  const brokeragePull = form.watch("brokeragePull");

  const { shorten } = formContext;

  useEffect(() => {
    getWindow()?.scrollTo({ top: 0 });
  }, []);

  const postDonationAction = usePostDonationAction({
    redirectUrl,
    onComplete,
    nonprofit,
    fundraiser,
  });

  // The embedded flow has no post-donation actions to click through, so
  // fire the completion action (e.g. notifying the embedding page) as soon
  // as the thank-you screen renders.
  const notifiedEmbedCompletion = useRef(false);
  useEffect(() => {
    if (
      formContext.embedMode &&
      createOrUpdateDonationResult &&
      !notifiedEmbedCompletion.current
    ) {
      notifiedEmbedCompletion.current = true;
      postDonationAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext.embedMode, createOrUpdateDonationResult]);

  const navigate = useNavigate();

  const email = form.watch("email");
  const commentTextString = form.watch("commentText");
  const commentText = commentTextCodec.is(commentTextString)
    ? commentTextString
    : undefined;

  if (!createOrUpdateDonationResult) {
    // TODO it would be cool if we could actually load this donation and show
    // the confirmation page
    navigate(DONATE_HASH, { replace: true });
    return null;
  }

  // The embedded flow never prompts for sign-in or account creation after
  // donating — just the thank-you message.
  if (formContext.embedMode) {
    return (
      <ThankYouPageContainer>
        <ThankYouMessage
          paymentMethod={createOrUpdateDonationResult.donation.paymentMethod}
          brokeragePull={brokeragePull}
        />
      </ThankYouPageContainer>
    );
  }

  if (loggedInOrGuestUser?.isGuestWithExistingEmail) {
    return (
      <DonationLogin form={form} postDonationAction={postDonationAction} />
    );
  }

  return isLoggedIn ? (
    <ThankYouPageContainer>
      <ThankYouMessage
        paymentMethod={createOrUpdateDonationResult.donation.paymentMethod}
        brokeragePull={brokeragePull}
      />
      <AddComment
        donateAction={donateAction}
        shorten={shorten}
        defaultComment={commentText || defaultComment}
        nonprofit={nonprofit}
        fundraiser={fundraiser}
        createOrUpdateDonationResult={createOrUpdateDonationResult}
        user={loggedInOrGuestUser}
        postDonationAction={postDonationAction}
      />
    </ThankYouPageContainer>
  ) : (
    <Signup
      redirectUrl={redirectUrl}
      abTestingId={abTestingId}
      createOrUpdateDonationResult={createOrUpdateDonationResult}
      userEmail={email}
      postDonationAction={postDonationAction}
      paymentMethod={createOrUpdateDonationResult.donation.paymentMethod}
    />
  );
};
